import { and, inArray, isNotNull } from 'drizzle-orm'
import { accountApiTable, accountTable } from '../../schema'
import type { DatabaseWithTransaction } from '../../types'

type GetAnalyzingTargetApiAccountsParameters = {
  database: DatabaseWithTransaction
  accountIds: number[]
}

type GetAnalyzingTargetApiAccountsReturnType = {
  id: number
  apiKey: string
  apiPassphrase?: string
  apiSecret?: string
  apiStartTime?: number
  apis: {
    id: number
    type: string
    subType?: string
  }[]
}[]

/**
 * - accountTable から accountIds に該当 & exchangeApiKey != null の row 取得
 * - 取得した row.id に accountId が含まれる accountApiTable の row 取得
 * - 取得したデータを整形して返す
 */
export const getAnalyzingTargetApiAccounts = async ({
  database: { database },
  accountIds,
}: GetAnalyzingTargetApiAccountsParameters): Promise<GetAnalyzingTargetApiAccountsReturnType> => {
  if (accountIds.length === 0) {
    return []
  }

  // Step 1: Get accounts with API keys
  const accountRows = await database
    .select({
      id: accountTable.id,
      exchangeApiKey: accountTable.exchangeApiKey,
      exchangeApiSecret: accountTable.exchangeApiSecret,
      exchangeApiPassword: accountTable.exchangeApiPassword,
      apiStartTime: accountTable.apiStartTime,
    })
    .from(accountTable)
    .where(
      and(
        inArray(accountTable.id, accountIds),
        isNotNull(accountTable.exchangeApiKey),
      ),
    )

  if (accountRows.length === 0) {
    return []
  }

  // Step 2: Get account API records for these accounts
  const targetAccountIds = accountRows.map((row) => row.id)
  const apiRows = await database
    .select({
      id: accountApiTable.id,
      accountId: accountApiTable.accountId,
      type: accountApiTable.type,
      subType: accountApiTable.subType,
    })
    .from(accountApiTable)
    .where(inArray(accountApiTable.accountId, targetAccountIds))

  // Step 3: Group API records by account ID
  const apisByAccountId = new Map<
    number,
    { id: number; type: string; subType?: string }[]
  >()
  for (const api of apiRows) {
    const apis = apisByAccountId.get(api.accountId) || []
    apis.push({
      id: api.id,
      type: api.type,
      subType: api.subType ?? undefined,
    })
    apisByAccountId.set(api.accountId, apis)
  }

  // Step 4: Build final result
  const result: GetAnalyzingTargetApiAccountsReturnType = []
  for (const row of accountRows) {
    if (!row.exchangeApiKey) {
      continue
    }
    result.push({
      id: row.id,
      apiKey: row.exchangeApiKey,
      apiPassphrase: row.exchangeApiPassword ?? undefined,
      apiSecret: row.exchangeApiSecret ?? undefined,
      apiStartTime: row.apiStartTime?.getTime() ?? undefined,
      apis: apisByAccountId.get(row.id) || [],
    })
  }
  return result
}
